<template>
  <div :class="['sidebar-root', { 'sidebar-collapsed': !sidebarVisible }]" style="width:320px; min-width:320px; max-width:320px;">
    <!-- 접힘(미니) 상태: 로고/새채팅/히스토리/확장 버튼만 -->
    <div v-if="!sidebarVisible" class="fixed left-0 top-0 flex flex-col items-center h-screen w-16 py-4 z-50">
      <button class="group relative mb-4 p-1 bg-transparent border-none" tabindex="-1" @click="handleLogoClick" title="새 채팅">
        <img src="@/assets/icons/logo_icon.png" alt="logo" class="w-5 h-5" />
      </button>
      <button class="group relative p-0 mb-4 bg-transparent border-none flex flex-col items-center" @click="handleManualToggle(true)">
        <img src="@/assets/icons/bar_side_rev.png" alt="expand" class="w-6 h-6" />
        <div class="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 whitespace-nowrap">확장</div>
      </button>
      <template v-if="showChatFeatures">
        <button class="group relative p-0 mb-4 bg-transparent border-none flex flex-col items-center" @click="$emit('new-chat')">
          <img src="@/assets/icons/side_new_chat.png" alt="new chat" class="w-7 h-7" />
          <div class="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 whitespace-nowrap">새 채팅</div>
        </button>
        <button class="group relative p-0 mb-4 bg-transparent border-none flex flex-col items-center" @click="$emit('view-all')">
          <img :src="showHistoryPanel ? require('@/assets/icons/back.png') : require('@/assets/icons/history.png')" :alt="showHistoryPanel ? 'back' : 'history'" class="w-7 h-7" />
          <div class="absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 whitespace-nowrap">{{ showHistoryPanel ? 'Chat Page' : 'Chat History' }}</div>
        </button>
      </template>
    </div>
    <!-- 확장(본체) 상태 -->
    <div v-else class="sidebar-content h-screen flex flex-col bg-blue-50" style="box-sizing: border-box; width:320px; min-width:320px; max-width:320px;">
      <!-- 상단: 로고+숨김/확장 버튼 sticky top-0 -->
      <div class="flex flex-row justify-between items-center px-6 pt-4 pb-3 sticky top-0 z-0 bg-blue-50" style="box-sizing: border-box;">
        <img src="@/assets/icons/logo.png" alt="Secure AI Logo" class="h-7 ml-0 cursor-pointer" @click="handleLogoClick" title="새 채팅" />
        <button class="sidebar-toggle-btn group relative" @click="handleManualToggle(false)">
          <img src="@/assets/icons/bar_side.png" alt="toggle sidebar" class="w-6 h-6 ml-8" />
          <div class="absolute right-4 ml-2 px-2 py-1 bg-gray-800 text-white text-sm rounded opacity-0 group-hover:opacity-100 whitespace-nowrap">
            숨김
          </div>
        </button>
      </div>
      <!-- 토큰 사용량/새 채팅 등 내부 컨텐츠: box-sizing, padding 통일 -->
      <div class="p-0 px-4 pl-4" style="box-sizing: border-box;">
        <TokenUsage :token-usage="tokenUsage" />
        <button v-if="showChatFeatures" @click="$emit('new-chat')" class="w-full px-0 flex items-center justify-center gap-2 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 mt-4" title="새 채팅">
          <span class="material-icons">add</span>
          <span>새 채팅</span>
        </button>
      </div>
      <!-- 중앙 컨텐츠만 스크롤 -->
      <div class="flex-1 px-0 overflow-y-auto" style="box-sizing: border-box; width:320px; min-width:320px; max-width:320px;">
        <template v-if="showChatFeatures">
          <div class="mt-2" style="width:100%; max-width:290px; margin:0 auto;">
            <ChatHistory
              :chats="chatHistory"
              :show-checkbox="false"
              :current-chat-id="currentChatId"
              :with-margin="false"
              :is-sidebar="true"
              :is-loading="isLoading"
              @select-chat="$emit('select-chat', $event)"
            />
          </div>
        </template>
        <template v-else>
          <div class="h-full flex items-center justify-center">
            <div v-if="playgroundInfoText" class="mx-4 my-6 px-3 py-2 rounded-lg bg-blue-50 text-l text-gray-700 text-center leading-relaxed whitespace-pre-line"
              style="min-width:260px; max-width:340px; margin:0 auto; white-space:pre-line; text-align:center;">
              {{ playgroundInfoText }}
            </div>
          </div>
        </template>
      </div>
      <!-- 하단: View All 등 sticky bottom-0 -->
      <div class="p-4 px-4 pl-4 sticky bottom-0 z-0 bg-blue-50" style="box-sizing: border-box;">
        <button v-if="showChatFeatures" @click="$emit('view-all')" class="w-full px-0 flex justify-center items-center gap-2 py-2 text-gray-700 bg-blue-100 rounded-lg hover:bg-blue-200 mb-4" :title="showHistoryPanel ? '채팅 페이지' : '전체 보기'">
          <span class="text-sm">{{ showHistoryPanel ? '← Chat Page' : 'View All →' }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import TokenUsage from '@/components/common/TokenUsage.vue'
import ChatHistory from '@/components/chat/ChatHistory.vue'
import { mapState, mapActions } from 'vuex'

export default {
  name: 'AppSidebar',
  components: { TokenUsage, ChatHistory },
  props: {
    tokenUsage: {
      type: Object,
      required: true
    },
    showChatFeatures: {
      type: Boolean,
      default: false
    },
    chatHistory: {
      type: Array,
      default: () => []
    },
    currentChatId: {
      type: String,
      default: null
    },
    debugMode: {
      type: Boolean,
      default: false
    },
    showHistoryPanel: {
      type: Boolean,
      default: false
    },
    playgroundInfoText: {
      type: String,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
    }
  },
  computed: {
    ...mapState(['sidebarVisible', 'userManuallyToggled', 'lastWidth'])
  },
  methods: {
    ...mapActions(['setSidebarVisible', 'setUserManuallyToggled', 'updateLastWidth']),
    handleManualToggle(visible) {
      this.setUserManuallyToggled(true);
      this.setSidebarVisible(visible);
    },
    handleResize() {
      const currentWidth = window.innerWidth;
      
      // 이전 너비 > 768px && 현재 너비 <= 768px: 무조건 숨김
      if (this.lastWidth > 768 && currentWidth <= 768) {
        this.setUserManuallyToggled(false);
        this.setSidebarVisible(false);
      }
      // 이전 너비 <= 768px && 현재 너비 > 768px: 무조건 확장
      else if (this.lastWidth <= 768 && currentWidth > 768) {
        this.setUserManuallyToggled(false);
        this.setSidebarVisible(true);
      }
      // 그 외의 경우: 현재 상태 유지 (userManuallyToggled 상태도 유지)
      
      // 현재 너비를 저장
      this.updateLastWidth(currentWidth);
    },
    handleLogoClick() {
      if (this.$route.path !== '/chat') {
        this.$router.push('/chat');
      }
      this.$emit('new-chat');
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  }
}
</script>

<style scoped>
.sidebar-root {
  min-width: 320px;
  max-width: 320px;
  width: 320px;
  box-sizing: border-box;
  scrollbar-gutter: stable both-edges;
  background: #ffffff;
  transition: width 0.3s cubic-bezier(0.4,0,0.2,1), padding 0.3s, background 0.3s;
  /* position: relative; */
  display: flex;
  flex-direction: column;
}
.sidebar-collapsed {
  width: 0 !important;
  min-width: 0 !important;
  max-width: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  overflow: hidden !important;
}
.sidebar-toggle-btn {
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

button:focus {
  outline: none !important;
  position: relative;
}

button:focus img {
  box-shadow: 0 0 0 1px rgba(0, 123, 255, 0.25);
  border-radius: 50%;
  padding: 1px;
}
</style> 